import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { useNavVisibility } from "./useNavVisibility";
import { MODULE_META, NAV_SECTIONS, isNavItemVisible, stripQuery, type NavItem, type NavSection } from "./nav-config";

export interface ActiveModule {
  key: string;
  /** The sidebar row to highlight: the module itself, or the module it lives
   * under (NavSection.sidebarUnder). */
  sidebarKey: string;
  label: string;
  icon: string;
  description?: string;
  /** The module's items this caller can actually see, in nav-config order. */
  items: NavItem[];
}

/**
 * Which module owns the current route, derived purely from the URL - never
 * from anything clicked. A refresh or a direct deep link into any of a
 * module's pages therefore resolves to the same module as clicking its
 * sidebar entry would have, with no separate "which module is open" state
 * to drift out of sync with the page on screen.
 *
 * Shared by Sidebar (to highlight the active module) and ModuleHeader (to
 * render that module's title and tabs), so the two can never disagree
 * about where the caller currently is.
 */
export function useActiveModule(): ActiveModule | undefined {
  const location = useLocation();
  const ctx = useNavVisibility();

  return useMemo(() => {
    // Pick the section whose owning item has the LONGEST matching path, not
    // the first section declared in NAV_SECTIONS that owns the route at
    // all. A short shortcut link (e.g. the Integrations hub's `/biometric`
    // entry, a discoverability pointer into the real Biometric module) is a
    // path-prefix of every `/biometric/*` subroute, so first-match order
    // let it "own" the whole Biometric module and hide its real tab strip
    // (Devices/Gateways/Enrollment/Punches/Sync/Logs/Health/Reports/
    // Settings) behind a 2-item Integrations header instead. Longest-match
    // ownership is also the correct general rule for any other section that
    // links to another module's route as a shortcut.
    let best: { section: NavSection; visible: NavItem[]; matchLength: number; familySize: number } | undefined;
    for (const section of NAV_SECTIONS) {
      if (!section.moduleKey) continue;
      const visible = section.items.filter((item) => isNavItemVisible(item, ctx));
      const matchLength = visible.reduce((max, item) => {
        const path = stripQuery(item.to);
        const owns = location.pathname === path || location.pathname.startsWith(path + "/");
        return owns ? Math.max(max, path.length) : max;
      }, -1);
      if (matchLength < 0) continue;
      // On an equal match the section that mostly lives under the route's own
      // area wins. Finance lists "/expenses/finance?tab=approved" as a
      // shortcut into Expenses; it matches exactly as long as Expenses' own
      // entry for the same page, and declaring Finance first used to make the
      // Expenses tab "Approved Expenses" open inside the Finance module.
      // Measured as a share of the section, not a count, so a big section
      // with a couple of shortcuts into another area cannot outvote the
      // small module that actually lives there.
      const family = location.pathname.split("/")[1] ?? "";
      const familySize =
        visible.filter((item) => stripQuery(item.to).split("/")[1] === family).length / visible.length;
      if (!best || matchLength > best.matchLength || (matchLength === best.matchLength && familySize > best.familySize)) {
        best = { section, visible, matchLength, familySize };
      }
    }
    if (!best) return undefined;
    const meta = MODULE_META[best.section.moduleKey!];
    return {
      key: best.section.moduleKey!,
      sidebarKey: best.section.sidebarUnder ?? best.section.moduleKey!,
      label: meta?.label ?? best.section.title ?? best.section.moduleKey!,
      icon: meta?.icon ?? "layers",
      description: meta?.description,
      items: best.visible,
    };
  }, [location.pathname, ctx]);
}
